import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Quick filter chips that toggle the same collection filter params as the sidebar.
 *
 * Builds URLs from the live location so multi-select stays correct.
 * Does not re-render the section on filter updates — that would drop or disable
 * chips Shopify marks as count 0. Active state is synced from the URL only.
 *
 * @extends {Component}
 */
class QuickFiltersComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#onClick);
    document.addEventListener(StandardEvents.collectionUpdate, this.#syncFromURL);
    window.addEventListener('popstate', this.#syncFromURL);
    this.#syncFromURL();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#onClick);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#syncFromURL);
    window.removeEventListener('popstate', this.#syncFromURL);
  }

  /**
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const chip = target.closest('[data-quick-filter-chip]');
    if (!(chip instanceof HTMLButtonElement) || chip.disabled) return;
    if (!this.contains(chip)) return;

    event.preventDefault();
    this.#toggleChip(chip);
  };

  /**
   * @param {HTMLButtonElement} chip
   */
  #toggleChip(chip) {
    const isActive = chip.dataset.active === 'true';
    const nextUrl = this.#buildToggleUrl(chip, isActive);
    if (!nextUrl) return;

    this.#setChipActive(chip, !isActive);
    this.#applyUrl(nextUrl);
  }

  /**
   * Build add/remove URL from the current location so other active filters are preserved.
   * @param {HTMLButtonElement} chip
   * @param {boolean} isActive
   * @returns {string | null}
   */
  #buildToggleUrl(chip, isActive) {
    const paramName = chip.dataset.paramName;
    const value = chip.dataset.value;
    if (!paramName || value == null) return null;

    const url = new URL(window.location.href);
    const current = url.searchParams.getAll(paramName);

    url.searchParams.delete(paramName);
    url.searchParams.delete('page');

    if (isActive) {
      for (const entry of current) {
        if (entry !== value) url.searchParams.append(paramName, entry);
      }
    } else {
      for (const entry of current) {
        url.searchParams.append(paramName, entry);
      }
      if (!current.includes(value)) {
        url.searchParams.append(paramName, value);
      }
    }

    return `${url.pathname}${url.search}`;
  }

  /**
   * @param {string} url
   */
  #applyUrl(url) {
    const facetsForm = document.querySelector('facets-form-component');

    if (
      facetsForm &&
      'updateFiltersByURL' in facetsForm &&
      typeof facetsForm.updateFiltersByURL === 'function'
    ) {
      facetsForm.updateFiltersByURL(url);
      return;
    }

    window.location.assign(url);
  }

  /**
   * @param {HTMLButtonElement} chip
   * @param {boolean} active
   */
  #setChipActive(chip, active) {
    chip.dataset.active = active ? 'true' : 'false';
    chip.setAttribute('aria-pressed', active ? 'true' : 'false');
    chip.classList.toggle('quick-filters__chip--active', active);
  }

  /**
   * Keep chip pressed state aligned with the current URL after sidebar/facet changes.
   */
  #syncFromURL = () => {
    const params = new URL(window.location.href).searchParams;
    const chips = this.querySelectorAll('[data-quick-filter-chip]');

    for (const chip of chips) {
      if (!(chip instanceof HTMLButtonElement)) continue;
      const paramName = chip.dataset.paramName;
      const value = chip.dataset.value;
      if (!paramName || value == null) continue;

      const active = params.getAll(paramName).includes(value);
      this.#setChipActive(chip, active);
    }
  };
}

if (!customElements.get('quick-filters-component')) {
  customElements.define('quick-filters-component', QuickFiltersComponent);
}
